import { useState, useEffect } from "react";
import ProductAssessmentItem from "./ProductAssessmentItem";
import AssessmentActions from "./AssessmentActions";
import ProductCancelNote from "./ProductCancelNote";
import BulkApprovalCheckbox from "./BulkApprovalCheckbox";
import ReasonNoteModal, { noteForSelectedReason } from "./ReasonNoteModal";
import { post } from "../../../../services/ApiService";
import toast, { Toaster } from "react-hot-toast";

const applyDefaultReason = (item, indicator, templates) => {
  const title =
    item.status === "approve"
      ? "Valid Order"
      : item.status === "cancel"
      ? "Buyer Cancelled"
      : item.title;
  return {
    ...item,
    title,
    note: noteForSelectedReason(title, item.note, indicator, templates),
  };
};

const ProductAssessments = ({
  assessment,
  order,
  canApprove,
  canDecline,
  canMarkPending,
  canRefund,
  canCancel,
  userId,
  setAssessment,
  setOrder,
  setOpenEscalations,
  idCheckSummary,
  setIdCheckSummary,
  productIds,
}) => {
  const [approvedPro, setApprovedPro] = useState([]);
  const [cancelledPro, setCancelledPro] = useState([]);
  const [pendingPro, setPendingPro] = useState([]);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isCancelRefund, setIsCancelRefund] = useState(false);
  const [isMarkPending, setIsMarkPending] = useState(false);
  const [isAllApproved, setIsAllApproved] = useState(false);
  const [cancelReasons, setCancelReasons] = useState({});
  const [reasons, setReasons] = useState([]);
  const [currentProductId, setCurrentProductId] = useState(null);
  const allProductsProcessed =
    approvedPro.length + cancelledPro.length === productIds.length &&
    pendingPro.length === 0;
  const allProductsMarkedPending =
    productIds.length > 0 &&
    pendingPro.length === productIds.length;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [reasonTemplates, setReasonTemplates] = useState([]);
  const currentReason = reasons.find((r) => r.productId === currentProductId);
  const [isBulkAction, setIsBulkAction] = useState(false);

  useEffect(() => {
    if (currentReason?.status) {
      setReasons((prev) =>
        prev.map((item) =>
          item.productId === currentProductId
            ? applyDefaultReason(item, order?.indicator, reasonTemplates)
            : item
        )
      );
    }
  }, [currentReason?.status, currentProductId, order?.indicator, reasonTemplates]);

  const handleCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    setIsAllApproved(isChecked);

    if (isChecked) {
      setIsBulkAction(true);
      setIsModalVisible(true);
      setCurrentProductId("BULK_ACTION");
      setReasons([
        {
          productId: "BULK_ACTION",
          status: "approve",
          title: "",
          note: "",
        },
      ]);
    } else {
      setApprovedPro([]);
      setPendingPro([]);
    }
  };
  const handleSubmit = async (e) => {
    try {
      const Body = {
        approved: approvedPro,
        canceled: cancelledPro,
        reasons: reasons,
        assessmentId: assessment?._id,
        orderId: order._id,
        userId,
      };
      setIsSubmit(true);
      const res = await post(`api/orders/p-med/assessment/handling`, Body);
      setIsSubmit(false);
      if (res.success) {
        toast.success(res.message);
        setAssessment(res.assessment);
        if (setOrder && res.order) {
          setOrder(res.order);
        }
        if (Array.isArray(res.openEscalations) && setOpenEscalations) {
          setOpenEscalations(res.openEscalations);
        }
        setApprovedPro([]);
        setCancelledPro([]);
        setReasons([]);
      } else {
        if (res.idCheckSummary && setIdCheckSummary) {
          setIdCheckSummary(res.idCheckSummary);
        }
        toast.error(res.error || res.message || "Failed to update assessment");
      }
    } catch (error) {
      setIsSubmit(false);
      const data = error?.response?.data;
      if (data?.idCheckSummary && setIdCheckSummary) {
        setIdCheckSummary(data.idCheckSummary);
      }
      toast.error(
        data?.error || data?.message || "An error occurred. Please try again.",
      );
    }
  };

  const markProductPending = (productId) => {
    const id = String(productId);
    setPendingPro((prev) => [...new Set([...prev.map(String), id])]);
    setApprovedPro((prev) => prev.filter((item) => String(item) !== id));
    setCancelledPro((prev) => prev.filter((item) => String(item) !== id));
  };

  const handleSubmitPending = async () => {
    try {
      setIsMarkPending(true);
      const res = await post("api/orders/status/pending", { orderId: order._id });
      if (res.success) {
        toast.success(res.message);
        if (setOrder && res.order) {
          setOrder(res.order);
        }
        if (setAssessment) {
          setAssessment((prev) => ({
            ...prev,
            status: "COMPLETE",
            completedAt: res.assessment?.completedAt || new Date().toISOString(),
          }));
        }
        setPendingPro([]);
      } else {
        toast.error(res.error || "Failed to mark order as pending");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsMarkPending(false);
    }
  };

  const handleCancelRefund = async () => {
    try {
      const Body = {
        canceled: cancelledPro,
        reasons: reasons,
        assessmentId: assessment?._id,
        orderId: order._id,
        userId,
      };
      setIsCancelRefund(true);
      const res = await post("api/orders/p-med/cancel-refund", Body);
      setIsCancelRefund(false);
      if (res.success) {
        toast.success(res.message);
        setAssessment(res.assessment);
        if (setOrder && res.order) {
          setOrder(res.order);
        }
        setApprovedPro([]);
        setCancelledPro([]);
        setReasons([]);
      } else {
        toast.error(`Failed to update assessment`);
      }
    } catch (error) {
      setIsSubmit(false);
      toast.error("An error occurred. Please try again.");
    }
  };



  const openAssessmentActionModal = (productId, status) => {
    setIsModalVisible(true);
    setCurrentProductId(productId);
    setReasons((prev) => {
      const index = prev.findIndex((item) => item.productId === productId);
      if (index !== -1) {
        const updatedReasons = [...prev];
        updatedReasons[index] = {
          productId,
          status,
          title: "",
          note: "",
        };
        return updatedReasons;
      } else {
        return [
          ...prev,
          {
            productId,
            status,
            title: "",
            note: "",
          },
        ];
      }
    });
  };

  const handleCancel = () => {
    setCurrentProductId(null);
    setIsModalVisible(false);
  };

  const handleOrderAssessmentConfirm = () => {
    const currentReason = reasons.find((r) => r.productId === currentProductId);

    if (!currentReason?.title || !currentReason?.status) {
      toast.error("Please select a reason.");
      return;
    }

    const { status, title, note } = currentReason;

    // Conditional note requirement
    const isNoteRequired =
      (status === "approve" && title !== "Valid Order") ||
      (status === "cancel" && title !== "Buyer Cancelled") ||
      (status === "approve" && order?.indicator === "Red");

    if (isNoteRequired && !note?.trim()) {
      toast.error("Note is required for the selected reason.");
      return;
    }

    if (currentProductId === "BULK_ACTION") {
      // Apply the same reason to all products
      const newReasons = productIds.map((productId) => ({
        productId,
        status,
        title,
        note,
      }));
      setReasons(newReasons);
      setApprovedPro(productIds);
      setCancelledPro([]);
      setPendingPro([]);
    } else {
      // Original single product logic
      if (status === "approve") {
        setApprovedPro((prev) => [...new Set([...prev, currentProductId])]);
        setCancelledPro((prev) => prev.filter((id) => id !== currentProductId));
        setPendingPro((prev) => prev.filter((id) => String(id) !== String(currentProductId)));
      } else if (status === "cancel") {
        setCancelledPro((prev) => [...new Set([...prev, currentProductId])]);
        setApprovedPro((prev) => prev.filter((id) => id !== currentProductId));
        setPendingPro((prev) => prev.filter((id) => String(id) !== String(currentProductId)));
      }
    }

    setIsModalVisible(false);
    setCurrentProductId(null);
    setIsBulkAction(false);
  };

  if (!assessment?.products?.length) return null;

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      {/* Bulk Approval Checkbox */}
      {(order.status === "PENDING" || order.status === "PENDING_REVIEW") &&
        assessment.products.length > 1 && (
        <BulkApprovalCheckbox
          isAllApproved={isAllApproved}
          handleCheckboxChange={handleCheckboxChange}
        />
      )}

      {/* Individual Product Assessments */}
      {assessment.products.map((proAssess, index) => (
        <ProductAssessmentItem
          key={proAssess.productId}
          proAssess={proAssess}
          idCheckSummary={idCheckSummary}
          index={index}
          assessment={assessment}
          order={order}
          approvedPro={approvedPro}
          cancelledPro={cancelledPro}
          pendingPro={pendingPro}
          canApprove={canApprove}
          canDecline={canDecline}
          canMarkPending={canMarkPending}
          markProductPending={markProductPending}
          openAssessmentActionModal={openAssessmentActionModal}
        />
      ))}

      {/* Submit Actions */}
      {(order.status === "PENDING" ||
        order.status === "PENDING_REVIEW" ||
        order.status === "ASSESSMENT_REQUIRED" ||
        order.status === "TO_CANCEL" ||
        assessment.status === "PENDING") && (
        <AssessmentActions
          allProductsProcessed={allProductsProcessed}
          allProductsMarkedPending={allProductsMarkedPending}
          handleSubmit={handleSubmit}
          isSubmit={isSubmit}
          canRefund={canRefund}
          canMarkPending={canMarkPending}
          handleSubmitPending={handleSubmitPending}
          isMarkPending={isMarkPending}
          handleCancelRefund={handleCancelRefund}
          isCancelRefund={isCancelRefund}
          order={order}
          cancelledPro={cancelledPro}
          approvedPro={approvedPro}
        />
      )}



      {/* Cancellation Notes */}
      {assessment.products.map(
        (product, index) =>
          product.isCancel && (
            <ProductCancelNote
              key={`cancel-note-${index}`}
              product={product}
              assessment={assessment}
            />
          )
      )}

      <ReasonNoteModal
        open={isModalVisible}
        onOk={handleOrderAssessmentConfirm}
        onCancel={handleCancel}
        status={currentReason?.status}
        reasonTitle={currentReason?.title}
        note={currentReason?.note}
        onReasonChange={(value) => {
          setReasons((prev) =>
            prev.map((item) =>
              item.productId === currentProductId
                ? {
                    ...item,
                    title: value,
                    note: noteForSelectedReason(
                      value,
                      item.note,
                      order?.indicator,
                      reasonTemplates
                    ),
                  }
                : item
            )
          );
        }}
        onTemplatesChange={setReasonTemplates}
        onNoteChange={(value) => {
          setReasons((prev) =>
            prev.map((item) =>
              item.productId === currentProductId
                ? { ...item, note: value }
                : item
            )
          );
        }}
      />
    </>
  );
};

export default ProductAssessments;
